<template>
  <div class="payment-page">
    <!-- 顶部导航栏 -->
    <div class="top-nav">
      <div class="nav-container">
        <div class="back-btn" @click="$router.go(-1)">
          <i class="el-icon-arrow-left"></i> {{ $t("back") }}
        </div>
        <div class="nav-title">{{ $t("confirmPayment") }}</div>
        <div class="empty-box"></div>
      </div>
    </div>
    <div class="payment-info">
      <div class="payment-container">
        <!-- 订单信息卡片 -->
        <div class="order-card card">
          <h3 class="card-title">
            <i class="el-icon-order"></i> {{ $t("orderInfo") }}
          </h3>
          <div class="order-info">
            <div class="info-row">
              <span class="info-label">{{ $t("orderNo") }}：</span>
              <span class="info-value">{{ orderData.orderNo }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t("routeName") }}：</span>
              <span class="info-value">{{ orderData.routeName }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t("departDest") }}：</span>
              <span class="info-value">
                {{ formatDate(orderData.departure) }} -
                {{ formatDate(orderData.destination) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t("travelDate") }}：</span>
              <span class="info-value">
                {{ formatDate(orderData.startDate) }} -
                {{ formatDate(orderData.endDate) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t("bookCount") }}：</span>
              <span class="info-value"
                >{{ orderData.adultCount }} {{ $t("adult") }}</span
              >
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t("roomCount") }}：</span>
              <span class="info-value"
                >{{ orderData.roomCount }} {{ $t("roomUnit") }}</span
              >
            </div>
          </div>
        </div>
        <!-- 支付金额卡片 -->
        <div class="amount-card card">
          <h3 class="card-title">
            <i class="el-icon-money"></i> {{ $t("payAmount") }}
          </h3>
          <div class="amount-container">
            <span class="amount-label">{{ $t("totalPay") }}：</span>
            <div class="amount-value">
              <span class="currency">¥</span>
              <span class="price">{{ orderData.totalAmount.toFixed(2) }}</span>
            </div>
          </div>
          <div class="amount-note">
            <i class="el-icon-info"></i>
            {{ $t("feeIncludeTip") }}
          </div>
        </div>
        <!-- 支付方式选择 -->
        <div class="payment-method-card card">
          <h3 class="card-title">
            <i class="el-icon-credit-card"></i> {{ $t("selectPayMethod") }}
          </h3>
          <div class="method-list">
            <div
              class="method-item"
              :class="{ active: selectedMethod === 'alipay' }"
              @click="selectedMethod = 'alipay'"
            >
              <div class="method-icon alipay-icon"></div>
              <div class="method-name">{{ $t("alipay") }}</div>
              <i
                class="el-icon-check"
                :class="{ show: selectedMethod === 'alipay' }"
              ></i>
            </div>
            <div
              class="method-item"
              :class="{ active: selectedMethod === 'wechat' }"
              @click="selectedMethod = 'wechat'"
            >
              <div class="method-icon wechat-icon"></div>
              <div class="method-name">{{ $t("wechatPay") }}</div>
              <i
                class="el-icon-check"
                :class="{ show: selectedMethod === 'wechat' }"
              ></i>
            </div>
          </div>
        </div>
        <!-- 关键：根据设备显示不同内容 -->
        <!-- 手机端：显示“跳转支付中”提示（无二维码） -->
        <div v-if="isMobile && isLoading" class="mobile-loading card">
          <i class="el-icon-loading"></i>
          <span
            >{{ $t("preparingPay") }}
            {{
              selectedMethod === "alipay" ? $t("alipay") : $t("wechatPay")
            }}...</span
          >
        </div>
        <!-- 电脑端：显示二维码 -->
        <div v-else>
          <!-- 电脑端二维码区域 -->
          <div
            class="qrcode-card card"
            v-if="isQrcodeReady && qrcodeUrl && !isQrcodeExpired"
          >
            <div
              class="countdown-container"
              :class="{ warning: remainingTime < 300 }"
            >
              <span class="countdown-label">{{ $t("qrcodeExpire") }}：</span>
              <span class="countdown-value">{{
                formatCountdown(remainingTime)
              }}</span>
              <span class="countdown-tip">{{ $t("timeoutRefresh") }}</span>
            </div>
            <div class="qrcode-wrapper">
              <div class="qrcode-container">
                <img
                  :src="qrcodeUrl"
                  :alt="$t('payQrcode')"
                  class="qrcode-img"
                />
                <div class="qr-mask" v-if="isScanned">
                  <div class="mask-content">
                    <i class="el-icon-success"></i>
                    <p>{{ $t("scannedPleasePay") }}</p>
                  </div>
                </div>
              </div>
              <div class="qrcode-tip">
                <p>
                  1.
                  {{
                    $t("openPayTool", {
                      method:
                        selectedMethod === "alipay"
                          ? $t("alipay")
                          : $t("wechatPay"),
                    })
                  }}
                </p>
                <p>2. {{ $t("scanQrcodePay") }}</p>
              </div>
            </div>
            <div class="qrcode-actions">
              <el-button type="text" class="refresh-btn" @click="refreshQrcode">
                <i class="el-icon-refresh"></i> {{ $t("refreshQrcode") }}
              </el-button>
              <el-button
                type="text"
                class="check-btn"
                @click="checkPaymentStatus"
              >
                <i class="el-icon-search"></i> {{ $t("paidAlready") }}
              </el-button>
            </div>
          </div>
          <!-- 2. 二维码过期提示 -->
          <div
            class="expired-tip card"
            v-else-if="isQrcodeExpired && selectedMethod"
          >
            <i class="el-icon-warning"></i>
            <span>{{ $t("qrcodeExpiredRefresh") }}</span>
            <el-button
              type="primary"
              size="small"
              class="reload-btn"
              @click="refreshQrcode"
            >
              {{ $t("refreshNow") }}
            </el-button>
          </div>
          <!-- 未选择支付方式提示 -->
          <div
            class="no-method-tip card"
            v-else-if="!selectedMethod && !isGenerating"
          >
            <i class="el-icon-info"></i>
            <span>{{ $t("selectPayMethod") }}</span>
          </div>
          <!-- 加载中提示 -->
          <div class="loading-tip card" v-else>
            <i class="el-icon-loading"></i>
            <span
              >{{
                $t("generatingQrcode", {
                  method:
                    selectedMethod === "alipay"
                      ? $t("alipay")
                      : $t("wechatPay"),
                })
              }}...</span
            >
          </div>
        </div>
      </div>
    </div>
    <!-- 支付结果弹窗 -->
    <el-dialog
      :title="$t('payResult')"
      :visible.sync="showResultDialog"
      width="80%"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <div class="result-container">
        <div
          class="result-icon"
          :class="{
            success: paymentResult === 'success',
            fail: paymentResult === 'fail',
          }"
        >
          <i v-if="paymentResult === 'success'" class="el-icon-check"></i>
          <i v-else-if="paymentResult === 'fail'" class="el-icon-close"></i>
          <i v-else class="el-icon-loading"></i>
        </div>
        <div class="result-title">
          <span v-if="paymentResult === 'success'">{{ $t("paySuccess") }}</span>
          <span v-else-if="paymentResult === 'fail'">{{
            $t("payUnfinished")
          }}</span>
          <span v-else>{{ $t("checkingPayStatus") }}...</span>
        </div>
        <div class="result-desc">
          <span v-if="paymentResult === 'success'">
            {{ $t("paySuccessTip") }}
          </span>
          <span v-else-if="paymentResult === 'fail'">
            {{ $t("payUnfinishedTip") }}
          </span>
          <span v-else> {{ $t("checkingStatusTip") }} </span>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          @click="handleResultAction"
          :disabled="!paymentResult"
        >
          <span v-if="paymentResult === 'success'">{{ $t("viewOrder") }}</span>
          <span v-else>{{ $t("backToDetail") }}</span>
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "TravelPayment",
  data() {
    return {
      // 订单数据（实际项目中从路由参数或接口获取）
      orderData: {},
      // 选中的支付方式（alipay/wechat）
      selectedMethod: "",
      // 支付二维码地址
      qrcodeUrl: "",
      // 二维码是否生成完成
      isQrcodeReady: false,
      // 是否正在生成二维码
      isGenerating: false,
      // 倒计时剩余时间（秒）- 15分钟=900秒
      remainingTime: 900,
      // 倒计时定时器
      countdownTimer: null,
      // 支付结果弹窗显示状态
      showResultDialog: false,
      // 支付结果（success/fail/null）
      paymentResult: null,
      isQrcodeExpired: false,
      currentPaySn: "",
      isScanned: false, // 是否已扫码（后端同步）
      scannedPollTimer: null, // 扫码状态查询定时器
      paymentPollTimer: null, // 新增：支付轮询定时器
      isLoading: false, //跳转支付中间页的加载状态
      isMobile: false, // 是否为手机 H5 端
      alipayH5Params: null, // 支付宝 H5 支付参数（action + formData）
      wechatH5Url: "", // 微信 H5 支付链接（非微信环境用）
    };
  },
  watch: {
    // 监听支付方式变化，自动生成二维码
    selectedMethod(newVal) {
      if (newVal) {
        // 1. 清除之前的查询定时器
        if (this.scannedPollTimer) {
          clearInterval(this.scannedPollTimer);
        }
        // 2. 重置状态
        this.isScanned = false; // 同步状态
        this.currentPaySn = "";
        this.isQrcodeExpired = false;
        this.alipayH5Params = null;
        this.wechatH5Url = "";
        if (this.isMobile) {
          // 手机端：直接获取支付参数并跳转
          this.generatePayOrderAndRedirect(newVal);
        } else {
          // 3. 生成新二维码
          this.generateQrcode();
        }
      }
    },
  },
  mounted() {
    window.scrollTo(0, 0);
  },
  created() {
    // 1. 初始化时判断设备：手机端/电脑端
    this.isMobile = this.judgeIsMobile();
    // 2. 获取订单数据
    this.orderData = this.$route.query.orderData
      ? JSON.parse(decodeURIComponent(this.$route.query.orderData))
      : this.getDefaultOrderData();
    // 初始化时检查是否有订单数据，无数据则返回上一页
    if (!this.orderData.orderNo) {
      this.$message.error(this.$t("orderInfoFail"));
      this.$router.go(-1);
    }
  },
  beforeDestroy() {
    // 清除定时器，避免内存泄漏
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    if (this.scannedPollTimer) clearInterval(this.scannedPollTimer);
    if (this.paymentPollTimer) clearInterval(this.paymentPollTimer); // 清除轮询定时器
  },
  methods: {
    // 判断是否为手机端（H5 场景）
    judgeIsMobile() {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /android|iphone|ipad|ipod|windows phone/.test(userAgent);
    },
    // 获取默认订单数据（用于开发测试）
    getDefaultOrderData() {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + this.$route.query.days * 1 || 3);
      return {
        orderNo: `TR${Date.now()}`,
        routeName: this.$route.query.routeName || this.$t("defaultRoute"),
        startDate: today,
        endDate: endDate,
        adultCount: this.$route.query.adultCount || 1,
        roomCount: this.$route.query.roomCount || 1,
        totalAmount: (this.$route.query.totalPrice || 1999) * 1,
      };
    },
    // 格式化日期
    formatDate(date) {
      if (!date) return "";
      // 兼容时间戳和Date对象
      const targetDate =
        typeof date === "object" && date instanceof Date
          ? date
          : new Date(date);
      // 检查日期是否有效
      if (isNaN(targetDate.getTime())) {
        return date.toString(); // 日期无效时直接返回原始值
      }
      return `${targetDate.getFullYear()}-${(targetDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${targetDate.getDate().toString().padStart(2, "0")}`;
    },
    // 格式化倒计时（分:秒）
    formatCountdown(seconds) {
      const min = Math.floor(seconds / 60);
      const sec = seconds % 60;
      return `${min.toString().padStart(2, "0")}:${sec
        .toString()
        .padStart(2, "0")}`;
    },
    // 手机端生成支付单并跳转至PaymentPage
    generatePayOrderAndRedirect(payMethod) {
      this.isGenerating = true; // 显示加载状态
      // 调用后端接口生成支付单（获取pay_sn、expire_time、sign等参数）
      this.$post("/payment/create_pay_order", {
        order_no: this.orderData.orderNo, // 原订单号
        pay_method: payMethod, // 选择的支付方式
        is_mobile: 1, // 标记为手机端
      })
        .then((res) => {
          this.isGenerating = false;
          if (res.data.code === 1) {
            const { pay_sn, expire_time, sign } = res.data.data;
            // 跳转至PaymentPage，携带必要参数
            this.$router.push({
              path: "/payment/middle", // 确保此路由指向PaymentPage组件
              query: {
                pay_sn: pay_sn, // 支付单号（关键参数）
                pay_method: payMethod, // 支付方式
                expire_time: expire_time, // 过期时间
                sign: sign, // 签名（用于验证参数合法性）
                is_h5: 1, // 标记为手机端
              },
            });
          } else {
            this.$message.error(res.data.msg || this.$t("createPayOrderFail"));
          }
        })
        .catch((err) => {
          this.isGenerating = false;
          console.error("创建支付单失败：", err);
          this.$message.error(this.$t("networkError"));
        });
    },
    //生成支付二维码
    generateQrcode() {
      this.isGenerating = true;
      this.isQrcodeReady = false;
      this.isQrcodeExpired = false;
      this.$post("/payment/generate_qrcode", {
        order_no: this.orderData.orderNo,
        pay_method: this.selectedMethod,
      })
        .then((res) => {
          if (res.data.code === 1 && res.data.data.qr_url) {
            // 1. 记录当前二维码的唯一标识：paySn
            this.currentPaySn = res.data.data.pay_sn;
            // 2. 生成二维码 URL（略）
            this.qrcodeUrl =
              res.data.data.qr_url + "?t=" + new Date().getTime();
            // 3. 立即查询一次扫码状态
            this.queryScannedStatus();
            // 4. 启动定时查询（每2秒查一次，避免频繁请求）
            this.startScannedPoll();
            this.isQrcodeReady = true;
            this.isGenerating = false;
            this.remainingTime = 900;
            this.startCountdown();
            this.$message.success(
              `${this.$t("generatedQrcode")}${
                this.selectedMethod === "alipay"
                  ? this.$t("alipay")
                  : this.$t("wechatPay")
              }`,
            );
          } else {
            // 后端返回错误，提示用户并重置状态
            this.isGenerating = false;
            this.$message.error(res.data.msg || this.$t("qrcodeGenerateFail"));
          }
        })
        .catch((err) => {
          this.isGenerating = false;
          console.error("二维码请求失败：", err);
          this.$message.error(this.$t("requestError"));
        });
    },
    // 启动倒计时
    startCountdown() {
      // 清除现有定时器
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      // 每秒减少1秒
      this.countdownTimer = setInterval(() => {
        this.remainingTime--;
        // 倒计时结束
        if (this.remainingTime <= 0) {
          clearInterval(this.countdownTimer);
          this.isQrcodeReady = false;
          this.isQrcodeExpired = true;
          clearInterval(this.countdownTimer);
          localStorage.removeItem("paymentScanned"); // 清除存储
          this.$message.warning(this.$t("qrcodeExpiredTip"));
        }
      }, 1000);
    },
    // 刷新二维码
    refreshQrcode() {
      localStorage.removeItem("paymentScanned");
      this.generateQrcode();
    },
    // 启动定时查询（每2秒一次）
    startScannedPoll() {
      // 先清除之前的定时器，避免重复
      if (this.scannedPollTimer) {
        clearInterval(this.scannedPollTimer);
      }
      // 每2秒查询一次
      this.scannedPollTimer = setInterval(() => {
        this.queryScannedStatus();
      }, 2000);
    },
    async queryScannedStatus() {
      if (!this.currentPaySn) return;
      try {
        const res = await this.$post("/payment/query_scanned", {
          pay_sn: this.currentPaySn,
        });
        if (res.data.code === 1) {
          // 同步后端返回的扫码状态
          this.isScanned = res.data.data.is_scanned;
          // 关键：已扫码后，立即停止轮询
          if (this.isScanned && this.scannedPollTimer) {
            clearInterval(this.scannedPollTimer);
            this.scannedPollTimer = null; // 清空定时器变量，避免后续误判
            // 延迟2秒后开启支付结果轮询
            setTimeout(() => {
              this.startPaymentPoll();
            }, 2000); // 2000毫秒 = 2秒
          }
        }
      } catch (err) {
        console.error("查询扫码状态失败：", err);
      }
    },
    // 开始轮询支付状态
    startPaymentPoll() {
      // this.showResultDialog = true;
      this.paymentResult = null; // 重置支付结果
      // 先清除之前的定时器，避免重复
      if (this.paymentPollTimer) {
        clearInterval(this.paymentPollTimer);
      }
      // 每3秒查询一次支付结果
      this.paymentPollTimer = setInterval(() => {
        this.checkPaymentStatus();
      }, 3000);
      // // 立即查询一次
      // this.checkPaymentStatus();
    },
    // 调用后端接口，检查支付状态
    checkPaymentStatus() {
      this.$get("/payment/check_status", {
        pay_sn: this.currentPaySn,
      })
        .then((res) => {
          if (res.data.code === 1) {
            const pay_status = res.data.data.pay_status;
            if (pay_status == 1) {
              // 支付成功：停止轮询 + 提示 + 跳转
              clearInterval(this.paymentPollTimer);
              this.paymentResult = "success";
              this.showResultDialog = true;
              this.$message.success(this.$t("paySuccess"));
            }
          }
        })
        .catch((err) => {
          console.error("查询支付状态失败：", err);
        });
    },
    // 处理支付结果弹窗操作
    handleResultAction() {
      this.showResultDialog = false;
      if (this.paymentResult === "success") {
        // 支付成功，跳转到订单详情页
        this.$router.push({
          path: "/about-me/my-listings/order-list",
          query: { refresh: "1" }, // 携带刷新标识
        });
      } else {
        this.$router.go(-1);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.payment-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-bottom: 20px;
}
/* 顶部导航栏 */
.top-nav {
  height: 44px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 15px;
  max-width: 1200px;
  margin: auto;
}
.back-btn {
  display: flex;
  align-items: center;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}
.back-btn .el-icon-arrow-left {
  margin-right: 4px;
  font-size: 16px;
}
.nav-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}
.empty-box {
  width: 60px;
}
.payment-info {
  padding-top: 54px;
  max-width: 1200px;
  margin: auto;
}
.payment-container {
  padding: 10px 15px;
}
/* 通用卡片样式 */
.card {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.card-title {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin-bottom: 12px;
}
.card-title .el-icon {
  margin-right: 6px;
  color: #1677ff;
}
/* 订单信息卡片 */
.order-info {
  .info-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-size: 13px;
    color: #666;
    margin-bottom: 8px;
  }
  .info-row:last-child {
    margin-bottom: 0;
  }
  .info-label {
    width: 150px;
    color: #999;
  }
  .info-value {
    flex: 1;
    color: #333;
  }
}
/* 支付金额卡片 */
.amount-container {
  display: flex;
  align-items: flex-end;
  margin-bottom: 10px;
}
.amount-label {
  font-size: 14px;
  color: #666;
  margin-right: 10px;
  margin-bottom: 2px;
}
.amount-value {
  display: flex;
  align-items: baseline;
}
.currency {
  font-size: 16px;
  color: #ff4d4f;
  margin-right: 2px;
}
.price {
  font-size: 24px;
  font-weight: 700;
  color: #ff4d4f;
}
.amount-note {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
}
.amount-note .el-icon-info {
  font-size: 12px;
  margin-right: 4px;
}
// 支付方式选择卡片
.method-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.method-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 10px;
  border-radius: 6px;
  border: 1px solid #eee;
  cursor: pointer;
  transition: all 0.2s;
}
.method-item.active {
  border-color: #1677ff;
  background-color: rgba(22, 119, 255, 0.05);
}
.method-icon {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  margin-right: 10px;
}
.alipay-icon {
  background: url("../../assets/images/alipay.svg") center center no-repeat;
  background-size: contain;
}
.wechat-icon {
  background: url("../../assets/images/wechatpay.svg") center center no-repeat;
  background-size: contain;
}
.method-name {
  flex: 1;
  font-size: 14px;
  color: #333;
}
.el-icon-check {
  font-size: 16px;
  color: #1677ff;
  opacity: 0;
  transition: opacity 0.2s;
}
.el-icon-check.show {
  opacity: 1;
}
// 二维码卡片
.qrcode-card {
  .countdown-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #eee;
  }
  .countdown-container.warning {
    .countdown-value {
      color: #ff4d4f;
    }
  }
  .countdown-label {
    color: #999;
  }
  .countdown-value {
    font-weight: 500;
    color: #333;
  }
  .countdown-tip {
    color: #999;
  }
  .qrcode-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    .qrcode-container {
      position: relative;
      width: 200px;
      height: 200px;
      margin: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 10px;
      background-color: #fff;
      padding: 10px;
      border: 1px solid #eee;
      border-radius: 6px;
      .qrcode-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }
  /* 蒙层样式 */
  .qr-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7); /* 半透明黑色背景 */
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10; /* 确保在二维码上方 */
    .mask-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      i {
        font-size: 36px;
        color: #409eff; /* 成功图标颜色 */
      }
      p {
        color: white;
        font-size: 14px;
        text-align: center;
        padding: 0 10px;
      }
    }
  }
  .qrcode-tip {
    font-size: 13px;
    color: #666;
    text-align: center;
    line-height: 1.5;
  }
  .qrcode-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
  }
  .el-button {
    font-size: 12px;
    color: #1677ff;
    padding: 0;
  }
}
.expired-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  color: #ff4d4f;
  font-size: 14px;
  gap: 12px;
  .el-icon-warning {
    font-size: 18px;
  }
  .reload-btn {
    height: 32px;
    padding: 0 16px;
    font-size: 12px;
  }
}
// 未选择支付方式提示和加载提示
.no-method-tip,
.loading-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
  color: #666;
  font-size: 14px;
}
.no-method-tip .el-icon-info {
  margin-right: 6px;
  color: #1677ff;
}
.loading-tip .el-icon-loading {
  margin-right: 8px;
  animation: rotating 2s linear infinite;
}
// 支付结果弹窗
.result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
}
.result-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}
.result-icon.success {
  background-color: rgba(46, 184, 126, 0.1);
}
.result-icon.success .el-icon-check {
  font-size: 32px;
  color: #2e9067;
}
.result-icon.fail {
  background-color: rgba(255, 77, 79, 0.1);
}
.result-icon.fail .el-icon-close {
  font-size: 32px;
  color: #ff4d4f;
}
.result-icon .el-icon-loading {
  font-size: 32px;
  color: #1677ff;
  animation: rotating 2s linear infinite;
}
.result-title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}
.result-desc {
  font-size: 13px;
  color: #666;
  text-align: center;
  line-height: 1.5;
}
.dialog-footer {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}
.dialog-footer .el-button {
  width: 120px;
  height: 36px;
  border-radius: 18px;
}
/* 新增：手机端跳转支付的加载提示样式 */
.mobile-loading {
  text-align: center;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.mobile-loading i {
  font-size: 24px;
  color: #409eff;
  margin-bottom: 12px;
  animation: spin 1.5s linear infinite;
}
.mobile-loading span {
  font-size: 16px;
  color: #333;
}
// 适配小屏幕手机
@media (max-width: 768px) {
  .payment-container {
    padding: 10px;
  }
  .payment-info {
    padding-top: 10px;
  }
  .qrcode-container {
    width: 180px !important;
    height: 180px !important;
  }
}
// 加载动画
@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
